import {useVaultUpdate} from "../../core/util/useVaultUpdate";
import {atParentAccount, atParentStudents} from "../../core/vault/StateCursors";
import {
	isUnregisteredStudent,
	cleanStudentName,
	isValidStudentName,
	isPlaceholderStudentDisplayName,
	transformStudentDataToStudent,
} from "./PrimaryRegistrationUtil";
import {getFeatureFlag} from "../../core/util/FeatureFlags";

export type ParentBookDeferral = {
	subscriptionID: string | number;
};

export const hasPurchasedDeferredBundle = (
	deferrals: readonly ParentBookDeferral[] | null | undefined
): boolean => !!(deferrals && deferrals.length > 0);

export const shouldSeePlacementTestChoice = (
	deferrals: readonly ParentBookDeferral[] | null | undefined
): boolean =>
	!!getFeatureFlag("ENABLE_STUDENT_ONBOARDING_PLACEMENT_TEST") ||
	hasPurchasedDeferredBundle(deferrals);

export {
	isUnregisteredStudent,
	cleanStudentName,
	isValidStudentName,
	isPlaceholderStudentDisplayName,
	transformStudentDataToStudent,
};

const PLACEHOLDER_NAME_PATTERN = /^Student [A-Z]+$/i;

// Rough order of steps, see getNextPrimarySetupStepV2 below
// for additional logic / experiment gating.
const STUDENT_PRIMARY_SETUP_STEPS_PLACEMENT_TEST_V2 = [
	"START",
	"WELCOME",
	"DISPLAY_NAME",
	"AGE",
	// We will only surface PLACEMENT_TEST_CHOICE when shouldSeePlacementTestChoice()
	// is true (A/B flag or deferred bundle purchase).
	"PLACEMENT_TEST_CHOICE",
	"BA_LEVEL",
	"PLACEMENT_TEST_DESIGNATED",
	"USERNAME_SELECTION",
	"USERNAME_CONFIRMATION",
	"PASSWORD_SELECTION",
	"FINAL",
] as const;

export type StudentPrimarySetupStepV2 = typeof STUDENT_PRIMARY_SETUP_STEPS_PLACEMENT_TEST_V2[number];

// Maps V2 step names to the server step name where they differ.
export const V2_SERVER_STEP: Partial<Record<
	StudentPrimarySetupStepV2,
	string
>> = {
	AGE: "BIRTHDAY",
};

// Roughly forward order of PrimarySetupStepV2, with some experiment branches
export const getNextPrimarySetupStepV2 = (
	currentStep: StudentPrimarySetupStepV2,
	deferrals: readonly ParentBookDeferral[] | null | undefined = null
): StudentPrimarySetupStepV2 | null => {
	if (currentStep === "START") return "WELCOME";
	if (currentStep === "WELCOME") return "DISPLAY_NAME";
	if (currentStep === "DISPLAY_NAME") return "AGE";
	if (currentStep === "AGE") {
		if (shouldSeePlacementTestChoice(deferrals)) {
			return "PLACEMENT_TEST_CHOICE";
		}
		return "BA_LEVEL";
	}
	// PLACEMENT_TEST_CHOICE branches — handled explicitly by step components
	if (currentStep === "PLACEMENT_TEST_CHOICE") return null;
	if (
		currentStep === "BA_LEVEL" ||
		currentStep === "PLACEMENT_TEST_DESIGNATED"
	) {
		return "USERNAME_SELECTION";
	}
	if (currentStep === "USERNAME_SELECTION") return "USERNAME_CONFIRMATION";
	if (currentStep === "USERNAME_CONFIRMATION") return "PASSWORD_SELECTION";
	if (currentStep === "PASSWORD_SELECTION") return "FINAL";
	if (currentStep === "FINAL") return null;

	const exhaustiveCheck: never = currentStep;
	throw new Error(`Unhandled step case: ${exhaustiveCheck}`);
};

// Roughly reverse order of PrimarySetupStepV2, with some experiment branches
export const getPreviousPrimarySetupStepV2 = (
	currentStep: StudentPrimarySetupStepV2,
	studentData: any,
	deferrals: readonly ParentBookDeferral[] | null | undefined = null
): StudentPrimarySetupStepV2 | null => {
	const showPlacementTestChoice = shouldSeePlacementTestChoice(deferrals);

	if (currentStep === "FINAL") return null;
	if (currentStep === "PASSWORD_SELECTION") return "USERNAME_CONFIRMATION";
	if (currentStep === "USERNAME_CONFIRMATION") {
		if (!studentData.username) {
			return "USERNAME_SELECTION";
		} else {
			return showPlacementTestChoice ? "PLACEMENT_TEST_CHOICE" : "BA_LEVEL";
		}
	}
	if (currentStep === "USERNAME_SELECTION") {
		return showPlacementTestChoice ? "PLACEMENT_TEST_CHOICE" : "BA_LEVEL";
	}
	if (currentStep === "PLACEMENT_TEST_DESIGNATED")
		return "PLACEMENT_TEST_CHOICE";
	if (currentStep === "BA_LEVEL") {
		if (showPlacementTestChoice) {
			return "PLACEMENT_TEST_CHOICE";
		}
		return "AGE";
	}
	if (currentStep === "PLACEMENT_TEST_CHOICE") return "AGE";
	if (currentStep === "AGE") {
		const name = studentData?.displayName?.trim() || "";
		if (!PLACEHOLDER_NAME_PATTERN.test(name)) {
			return "WELCOME";
		} else {
			return "DISPLAY_NAME";
		}
	}
	if (currentStep === "DISPLAY_NAME") return "WELCOME";
	if (currentStep === "WELCOME") return "START";
	if (currentStep === "START") return null;

	const exhaustiveCheck: never = currentStep;
	throw new Error(`Unhandled step case: ${exhaustiveCheck}`);
};

// Progress bar uses 5 sections (indices 0–4). ProgressBar treats sectionIndex+0.5
// as the "completion threshold": currentStep > threshold → completed (dark green),
// currentStep === threshold → current (light green), else grey.
//
// Section layout:
//   0 = Name        (DISPLAY_NAME)
//   1 = Age           (AGE)
//   2 = Level         (BA_LEVEL, PLACEMENT_TEST_CHOICE and variants)
//   3 = Username      (USERNAME_SELECTION, USERNAME_CONFIRMATION)
//   4 = Password      (PASSWORD_SELECTION)
export const getProgressSectionByStepV2 = (
	step: StudentPrimarySetupStepV2
): number => {
	switch (step) {
		case "START":
			return 0;
		case "WELCOME":
			return 0;
		case "DISPLAY_NAME":
			return 0.5;
		case "AGE":
			return 1.5;
		case "PLACEMENT_TEST_CHOICE":
			return 2.5;
		case "PLACEMENT_TEST_DESIGNATED":
			return 2.5;
		case "BA_LEVEL":
			return 2.5;
		case "USERNAME_SELECTION":
			return 3.5;
		case "USERNAME_CONFIRMATION":
			return 3.5;
		case "PASSWORD_SELECTION":
			return 4.5;
		case "FINAL":
			return 5;
		default:
			return 0;
	}
};

export const getPrimarySetupStepByProgressBarSectionV2 = (
	sectionIndex: number
): StudentPrimarySetupStepV2 => {
	switch (sectionIndex) {
		case 0:
			return "DISPLAY_NAME";
		case 1:
			return "AGE";
		case 2:
			if (getFeatureFlag("ENABLE_STUDENT_ONBOARDING_PLACEMENT_TEST")) {
				return "PLACEMENT_TEST_CHOICE";
			}
			return "BA_LEVEL";
		case 3:
			return "USERNAME_SELECTION";
		case 4:
			return "PASSWORD_SELECTION";
		default:
			return "DISPLAY_NAME";
	}
};

// Mid-flow steps where a parent-data reload must not reset primarySetupStep back
// to START (V2 keeps level selection after password, etc.).
const IN_PROGRESS_PRIMARY_SETUP_STEPS_V2 = new Set<
	StudentPrimarySetupStepV2 | string
>([
	"WELCOME",
	"DISPLAY_NAME",
	"AGE",
	"BA_LEVEL",
	"USERNAME_SELECTION",
	"USERNAME_CONFIRMATION",
	"PASSWORD_SELECTION",
	"PLACEMENT_TEST_CHOICE",
	"PLACEMENT_TEST_DESIGNATED",
]);

export const isInProgressPrimarySetupStepV2 = (
	step: string | undefined
): boolean => {
	return !!step && IN_PROGRESS_PRIMARY_SETUP_STEPS_V2.has(step);
};

/**
 * Derive the best V2 step to restore to on a fresh page load, based on the
 * registration code payload returned by the server. Checkpoints we can detect:
 *
 *   (hasSetGrade || hasSetShouldTakePlacementTest) && !hasSetUsername → USERNAME_SELECTION
 *      - A/B Test 0, don't change the order of steps, and no placement test.
 *      - A/B Test 1, don't change the order of steps, but add placement test option.
 *      (In A/B Test 0, hasSetShouldTakePlacementTest will always be the false, so the above logic works for both cases)
 *   hasSetUsername && !hasSetPassword → PASSWORD_SELECTION
 *   student has a real display name  → AGE        (name was saved, skipping DISPLAY_NAME)
 *   otherwise                        → START
 */
export const getRestoredV2Step = (
	registrationStatus:
		| {
				hasSetGrade?: boolean;
				hasSetShouldTakePlacementTest?: boolean;
				hasSetUsername?: boolean;
				hasSetPassword?: boolean;
		  }
		| null
		| undefined,
	displayName: string | null | undefined
): StudentPrimarySetupStepV2 => {
	if (!registrationStatus) return "START";
	const {
		hasSetUsername,
		hasSetPassword,
		hasSetGrade,
		hasSetShouldTakePlacementTest,
	} = registrationStatus;
	if ((hasSetGrade || hasSetShouldTakePlacementTest) && !hasSetUsername)
		return "USERNAME_SELECTION";
	if (hasSetUsername && !hasSetPassword) return "PASSWORD_SELECTION";
	if (displayName && !isPlaceholderStudentDisplayName(displayName))
		return "AGE";
	return "START";
};

export const useUnregisteredStudentsV2 = (): any[] | null => {
	const students = useVaultUpdate({
		getValuesFromVault: vault => atParentStudents(vault).get(),
	});
	if (!students) return null;
	return Object.values(students).filter((student: any) =>
		isUnregisteredStudent(student)
	);
};

export const useParentBookDeferrals = (): ParentBookDeferral[] | null =>
	useVaultUpdate({
		getValuesFromVault: vault => atParentAccount(vault).getIn("deferrals"),
	});

// ─── Level metadata ───────────────────────────────────────────────────────────

export const LEVEL_DESCRIPTIONS: Record<number, string> = {
	1: "{name} will establish core foundations of counting, addition, subtraction, measurements, and more.",
	2: "{name} will strengthen place-value understanding, develop techniques for multi-digit addition and subtraction, and learn essential problem-solving strategies.",
	3: "{name} will tackle multiplication and division, learn the fundamentals of variables, and explore shapes, angles, and patterns.",
	4: "{name} will apply their arithmetic skills to negative numbers, fractions, and decimals. They'll also learn some non-standard topics like probability and logic.",
	5: "{name} will tackle some of the most complex topics in Beast Academy like equations, statistics, factors, ratios, 3D geometry and more!",
};

export const AGE_RANGES_BY_LEVEL: Record<number, [number, number]> = {
	1: [6, 8],
	2: [7, 9],
	3: [9, 10],
	4: [10, 12],
	5: [11, 13],
};

export const getAgeNumber = (age: string): number => {
	if (age === "under-6") return 5;
	if (age === "over-11") return 12;
	return parseInt(age);
};
